import { ButtonCtaPrimary } from '../../lib/ds/button/button';
import { Label } from '../../lib/ds/form/label';
import { Textarea } from '../../lib/ds/form/textarea';
import { BottomSheet } from '../../lib/ds/modal/bottom-sheet';
import { ModalActionBar, ModalContent } from '../../lib/ds/modal/modal';
import { ModalHeader } from '../../lib/ds/modal/modal-header';
import { RadioGroup } from '../../lib/ds/radiogroup/radiogroup';

import { isEmpty, isNull } from 'lodash-es';
import { useEffect, useState } from 'react';
import type { AufgabeAbschliessenModel } from './model';
import type { AufgabeErgebnis } from './utils/constants';
import { getErgebnisoptionen } from './utils/helper';

export const ergebnisLabelMapping: Record<AufgabeErgebnis, string> = {
  Erfolgreich: 'Erfolgreich abschließen',
  Erfolglos: 'Ohne Erfolg abschließen',
  Zwischenergebnis: 'Zwischenergebnis erfassen',
};

type ErgebnisOptionenModalProps = {
  aufgabe: AufgabeAbschliessenModel;
  ergebnis: AufgabeErgebnis | null;
  onClose: () => void;
  onErgebnisErfasst: (ergebnisOption: string, kommentar: string) => void;
};

export const ErgebnisOptionenModal = ({ aufgabe, ergebnis, onClose, onErgebnisErfasst }: ErgebnisOptionenModalProps) => {
  const ergebnisOptionen = getErgebnisoptionen(ergebnis, aufgabe);
  const [ergebnisOption, setErgebnisOption] = useState<string>('');
  const [kommentar, setKommentar] = useState<string>('');

  useEffect(() => {
    const option = (ergebnis === 'Zwischenergebnis' && aufgabe.ergebnisOption) ? aufgabe.ergebnisOption : '';
    const kommentar = (ergebnis === 'Zwischenergebnis' && aufgabe.kommentar) ? aufgabe.kommentar : '';
    setErgebnisOption(option);
    setKommentar(kommentar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ergebnis]);

  // workaround if the options will change or an undefined option was set by the backend
  if (!ergebnisOptionen.some(opt => opt === aufgabe.ergebnisOption) && aufgabe.ergebnisOption && ergebnis === 'Zwischenergebnis') {
    ergebnisOptionen.push(aufgabe.ergebnisOption);
  }

  const handleErgebnisErfassen = () => {
    setErgebnisOption('');
    setKommentar('');
    onErgebnisErfasst(ergebnisOption, kommentar);
  };

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const title = ergebnisLabelMapping[ergebnis!];

  return (
    <BottomSheet onClose={onClose} isOpen={!isNull(ergebnis)} label={title ?? 'Ergebnis erfassen'}>
      <ModalHeader
        title={title}
        onClose={onClose}
        text={ergebnis !== 'Zwischenergebnis' ? 'Sind weitere Schritte zur Verfolgung des Auftrags notwendig und wenn ja, welche?' : undefined}/>
      <ModalContent className="flex flex-col gap-300">
        {isNull(ergebnis) ? null : (<>
          <RadioGroup
            onOptionChange={setErgebnisOption}
            selectedOption={ergebnisOption}
            options={ergebnisOptionen}
          />
          <Label label="Kommentare" labelPosition="top">
            <Textarea
              value={kommentar}
              rows={4}
              className="resize-none"
              placeholder="Kommentieren Sie die Aufgabe für den Innendienstmitarbeiter..."
              onValueChange={value => setKommentar(value ?? '')} />
          </Label>
        </>)}
      </ModalContent>
      <ModalActionBar>
        <ButtonCtaPrimary
          onClick={() => handleErgebnisErfassen()}
          disabled={isEmpty(ergebnisOption)}
          className="w-full"
        >
          Ergebnis erfassen
        </ButtonCtaPrimary>

      </ModalActionBar>
    </BottomSheet>
  );
};


export const useErgebnisOptionenModal = () => {
  const [ergebnis, setErgebnis] = useState<AufgabeErgebnis | null>(null);
  const closeModal = () => setErgebnis(null);
  const openModal = (ergebnis: AufgabeErgebnis) => setErgebnis(ergebnis);
  return {
    ergebnis,
    closeModal,
    openModal,
  };
};
